import { createApi } from '@reduxjs/toolkit/query/react'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'
import type {
  GatewayLinkReadMode,
  GatewayLinkRoute,
  GatewayLinkRouteInput,
  GatewayLinkWriteMode
} from './types'

interface GatewayLinkRouteWire {
  gateway_id: string
  gateway_url: string
  acquisition_mode: GatewayLinkReadMode
  tag_read_mode: GatewayLinkReadMode
  history_mode: GatewayLinkReadMode
  alarm_mode: GatewayLinkReadMode
  opc_mode: GatewayLinkReadMode
  database_mode: GatewayLinkReadMode
  health_mode: GatewayLinkReadMode
  write_mode: GatewayLinkWriteMode
  created_at: string
  updated_at: string
  online?: boolean
  session_id?: string
  fence_epoch?: number
  session_expires_at?: string
  negotiated_capabilities?: string[]
}

interface GatewayLinkRoutesResponse {
  routes?: GatewayLinkRouteWire[]
}

interface GatewayLinkRouteResponse {
  route: GatewayLinkRouteWire
}

function mapGatewayLinkRoute(route: GatewayLinkRouteWire): GatewayLinkRoute {
  return {
    gatewayId: route.gateway_id,
    gatewayUrl: route.gateway_url,
    acquisitionMode: route.acquisition_mode,
    tagReadMode: route.tag_read_mode,
    historyMode: route.history_mode,
    alarmMode: route.alarm_mode,
    opcMode: route.opc_mode,
    databaseMode: route.database_mode,
    healthMode: route.health_mode,
    writeMode: route.write_mode,
    createdAt: route.created_at,
    updatedAt: route.updated_at,
    online: route.online === true,
    sessionId: route.session_id ?? null,
    fenceEpoch: route.fence_epoch ?? null,
    sessionExpiresAt: route.session_expires_at ?? null,
    negotiatedCapabilities: route.negotiated_capabilities ?? []
  }
}

export function gatewayLinkRouteRequest(route: GatewayLinkRouteInput): Record<string, string> {
  return {
    gateway_id: route.gatewayId.trim(),
    gateway_url: route.gatewayUrl.trim().replace(/\/+$/, ''),
    acquisition_mode: route.acquisitionMode,
    tag_read_mode: route.tagReadMode,
    history_mode: route.historyMode,
    alarm_mode: route.alarmMode,
    opc_mode: route.opcMode,
    database_mode: route.databaseMode,
    health_mode: route.healthMode,
    write_mode: route.writeMode
  }
}

export const gatewayLinkApi = createApi({
  reducerPath: 'gatewayLinkApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: ['GatewayLinkRoute'],
  endpoints: (builder) => ({
    configureGatewayLink: builder.mutation<
      Record<string, string | number>,
      { gatewayUrl: string; certificate: string; gatewayId?: string; siteId: string }
    >({
      query: ({ gatewayUrl, certificate, gatewayId, siteId }) => ({
        url: `${PROXY_API_V1_URL}/gateway-link/configure`,
        method: 'POST',
        headers: { 'X-Ontology-Site': siteId },
        body: {
          gateway_url: gatewayUrl,
          gateway_certificate: certificate,
          ...(gatewayId?.trim() ? { gateway_id: gatewayId.trim() } : {})
        }
      })
    }),
    listGatewayLinkRoutes: builder.query<GatewayLinkRoute[], void>({
      query: () => `${PROXY_API_V1_URL}/gateway-link/routes`,
      transformResponse: (response: GatewayLinkRoutesResponse | null) =>
        (response?.routes ?? []).map(mapGatewayLinkRoute),
      providesTags: (result) =>
        result
          ? [
              ...result.map((route) => ({
                type: 'GatewayLinkRoute' as const,
                id: route.gatewayId
              })),
              { type: 'GatewayLinkRoute' as const, id: 'LIST' }
            ]
          : [{ type: 'GatewayLinkRoute' as const, id: 'LIST' }]
    }),
    upsertGatewayLinkRoute: builder.mutation<GatewayLinkRoute, GatewayLinkRouteInput>({
      query: (route) => ({
        url: `${PROXY_API_V1_URL}/gateway-link/routes`,
        method: 'POST',
        body: gatewayLinkRouteRequest(route)
      }),
      transformResponse: (response: GatewayLinkRouteResponse) =>
        mapGatewayLinkRoute(response.route),
      invalidatesTags: (result, _error, route) => [
        { type: 'GatewayLinkRoute', id: result?.gatewayId ?? route.gatewayId },
        { type: 'GatewayLinkRoute', id: 'LIST' }
      ]
    }),
    deleteGatewayLinkRoute: builder.mutation<GatewayLinkRoute, string>({
      query: (gatewayId) => ({
        url: `${PROXY_API_V1_URL}/gateway-link/routes:delete`,
        method: 'POST',
        body: { gateway_id: gatewayId }
      }),
      transformResponse: (response: GatewayLinkRouteResponse) =>
        mapGatewayLinkRoute(response.route),
      invalidatesTags: (_result, _error, gatewayId) => [
        { type: 'GatewayLinkRoute', id: gatewayId },
        { type: 'GatewayLinkRoute', id: 'LIST' }
      ]
    })
  })
})

export const {
  useConfigureGatewayLinkMutation,
  useListGatewayLinkRoutesQuery,
  useUpsertGatewayLinkRouteMutation,
  useDeleteGatewayLinkRouteMutation
} = gatewayLinkApi
